import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import pb from '../api/pocketbase';

const roleRouteMap = {
  'chefchantier': '/ChefChantierDashboard',
  'responsabletechnique': '/ResponsableTechniqueDashboard',
  'controleur': '/ComptableDashboard',
  'directeur': '/DgDashboard',
  'chargestructure': '/ChargeStructurePage',
  'immobilisations': '/ImmobilierPage'
};

const LoginForm = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Tous les champs doivent être remplis.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // 🔐 Étape 1 : Connexion à PocketBase
      await pb.collection('users').authWithPassword(username.trim(), password);

      // ✅ Étape 2 : Forcer la restauration complète de session
      await pb.collection('users').authRefresh();

      const user = pb.authStore.model;
      if (!user) {
        setError("Échec de l'authentification.");
        setLoading(false);
        return;
      }

      // 🧠 Déterminer le rôle (par champ ou fallback par email)
      let role = user.role || null;
      if (!role) {
        const email = user.email || '';
        if (email.includes('cc@')) role = 'chefchantier';
        else if (email.includes('rt@')) role = 'responsabletechnique';
        else if (email.includes('cg@')) role = 'controleur';
        else if (email.includes('dg@')) role = 'directeur';
        else if (email.includes('cs@')) role = 'chargestructure';
        else if (email.includes('im@')) role = 'immobilisations';
        else role = 'unknown';
      }

      if (role === 'unknown') {
        setError("Rôle utilisateur non reconnu.");
        setLoading(false);
        return;
      }

      // ✅ Connexion au AuthContext
      await login(user.email || username, role);

      console.log('🔐 Connexion réussie :', {
        email: user.email,
        role: role,
        token: pb.authStore.token,
        isValid: pb.authStore.isValid
      });

      // 🚀 Redirection vers le dashboard
      const route = roleRouteMap[role];
      if (route) {
        navigate(route);
      } else {
        setError("Route pour ce rôle non définie.");
      }

    } catch (err) {
      console.error('❌ Erreur lors de la connexion :', err);
      setError("Nom d'utilisateur ou mot de passe incorrect.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-gray-900 flex items-center justify-center px-4 relative"
      style={{
        backgroundImage: "url('/dywanes-logo.png')",
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlay foncé */}
      <div className="absolute inset-0 bg-black/60 z-0"></div>

      <div className="relative z-10 bg-white/90 backdrop-blur-lg shadow-xl rounded-3xl w-full max-w-md p-8 border border-gray-200">
        <div className="flex flex-col items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 tracking-wide text-center">
            Bienvenue chez DywaneImmo <span className="text-2xl">🏗️🏡</span>
          </h1>
          <p className="text-gray-600 text-sm mt-1">Connexion sécurisée à la plateforme</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm text-center rounded-md border border-red-200">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
              Nom d'utilisateur (email)
            </label>
            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 bg-white shadow-sm">
              <FontAwesomeIcon icon={faUser} className="text-gray-500 mr-3" />
              <input
                id="username"
                type="text"
                className="w-full bg-transparent outline-none text-sm text-gray-800 placeholder-gray-500"
                placeholder="Entrez votre email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Mot de passe
            </label>
            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 bg-white shadow-sm">
              <FontAwesomeIcon icon={faLock} className="text-gray-500 mr-3" />
              <input
                id="password"
                type="password"
                className="w-full bg-transparent outline-none text-sm text-gray-800 placeholder-gray-500"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold py-3 rounded-xl hover:brightness-110 transition-all duration-200 shadow-md disabled:opacity-70"
            disabled={loading}
          >
            {loading ? 'Connexion en cours...' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
